import React from 'react';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { 
  SiSlack, 
  SiSalesforce, 
  SiAmazonwebservices, 
  SiGooglecloud, 
  SiJira, 
  SiHubspot, 
  SiNotion, 
  SiZapier,
  SiOpenai,
  SiPython,
  SiGithub,
  SiMongodb,
  SiPostgresql,
  SiDocker,
  SiKubernetes,
  SiTerraform,
  SiDatadog,
  SiStripe,
  SiTwilio,
  SiMysql,
  SiRedis,
  SiElasticsearch,
  SiGrafana
} from 'react-icons/si';
import { Cloud } from 'lucide-react';

const integrations = [
  { icon: SiSlack, name: 'Slack', color: '#4A154B' },
  { icon: SiSalesforce, name: 'Salesforce', color: '#00A1E0' },
  { icon: SiAmazonwebservices, name: 'AWS', color: '#FF9900' },
  { icon: SiGooglecloud, name: 'Google Cloud', color: '#4285F4' },
  { icon: SiJira, name: 'Jira', color: '#0052CC' },
  { icon: SiHubspot, name: 'HubSpot', color: '#FF7A59' },
  { icon: SiNotion, name: 'Notion', color: '#FFFFFF' },
  { icon: SiZapier, name: 'Zapier', color: '#FF4A00' },
  { icon: SiOpenai, name: 'OpenAI', color: '#00A67E' },
  { icon: SiPython, name: 'Python', color: '#3776AB' },
  { icon: SiGithub, name: 'GitHub', color: '#FFFFFF' },
  { icon: Cloud, name: 'Azure', color: '#0078D4' },
];

const integrations2 = [
  { icon: SiMongodb, name: 'MongoDB', color: '#47A248' },
  { icon: SiPostgresql, name: 'PostgreSQL', color: '#4169E1' },
  { icon: SiDocker, name: 'Docker', color: '#2496ED' },
  { icon: SiKubernetes, name: 'Kubernetes', color: '#326CE5' },
  { icon: SiTerraform, name: 'Terraform', color: '#7B42BC' },
  { icon: SiDatadog, name: 'Datadog', color: '#632CA6' },
  { icon: SiStripe, name: 'Stripe', color: '#635BFF' },
  { icon: SiTwilio, name: 'Twilio', color: '#F22F46' },
  { icon: SiMysql, name: 'MySQL', color: '#4479A1' },
  { icon: SiRedis, name: 'Redis', color: '#DC382D' },
  { icon: SiElasticsearch, name: 'Elasticsearch', color: '#005571' },
  { icon: SiGrafana, name: 'Grafana', color: '#F46800' },
];

const IntegrationIcon = ({ icon: Icon, name, color }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.1, y: -5 }}
      className="flex flex-col items-center gap-2 mx-8 group cursor-pointer"
    >
      <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-slate-900/60 border border-emerald-500/20 flex items-center justify-center transition-all duration-300 group-hover:border-emerald-500/50 group-hover:bg-slate-800/60">
        <Icon className="w-7 h-7 md:w-8 md:h-8 text-slate-500 group-hover:text-white transition-colors duration-300" style={{ color: 'inherit' }} />
      </div>
      <span className="text-xs text-slate-600 group-hover:text-slate-400 transition-colors font-medium opacity-0 group-hover:opacity-100">
        {name}
      </span>
    </motion.div>
  );
};

const IntegrationMarquee = () => {
  return (
    <section
      data-testid="integration-marquee"
      className="relative py-20 border-y border-emerald-500/10 bg-slate-950/30 overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[200px] bg-emerald-500/5 rounded-full blur-[80px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-emerald-400 mb-4 block">
            Integration Ecosystem
          </span>
          <h2 className="text-3xl md:text-4xl font-bold font-outfit text-white mb-4">
            Full-Scale Integration, <span className="text-gradient">Zero Friction</span>
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Connect with 200+ tools and services. Your agents speak every protocol.
          </p>
        </motion.div>
      </div>

      {/* First marquee row */}
      <div className="marquee-container mb-6">
        <Marquee
          speed={40}
          gradient={true}
          gradientColor="#0f172a"
          gradientWidth={100}
          pauseOnHover={true}
        >
          {integrations.map((integration, index) => (
            <IntegrationIcon key={index} {...integration} />
          ))}
        </Marquee>
      </div>

      {/* Second marquee row (reverse direction) */}
      <div className="marquee-container">
        <Marquee
          speed={35}
          direction="right"
          gradient={true}
          gradientColor="#0f172a"
          gradientWidth={100}
          pauseOnHover={true}
        >
          {integrations2.map((integration, index) => (
            <IntegrationIcon key={index} {...integration} />
          ))}
        </Marquee>
      </div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        viewport={{ once: true }}
        className="max-w-7xl mx-auto px-6 mt-16"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: '200+', label: 'Integrations' },
            { value: '99.9%', label: 'Uptime SLA' },
            { value: '<100ms', label: 'Avg Response' },
            { value: '24/7', label: 'Support' },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl md:text-3xl font-bold font-outfit text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default IntegrationMarquee;
